import DefaultErrorPage from 'next/error'
import { useRouter } from 'next/router'
import { useState } from 'react'
import {
  Button,
  Collapse,
  Container,
  Navbar,
  Offcanvas,
  Stack,
} from 'react-bootstrap'
import { FaCaretDown, FaCaretLeft, FaCaretRight } from 'react-icons/fa'
import { CopyLinkButton } from 'web_core/assets/CopyLinkButton'
import { HeadProps } from 'web_core/assets/Head'
import { IconWrapper } from 'web_core/assets/IconWrapper'
import { PrintButton } from 'web_core/assets/PrintButton'
import { ArticleTitleLink } from 'web_core/assets/article'
import { BookImage } from 'web_core/assets/book'
import { BookImageWithTitle } from 'web_core/assets/book/ImageWithTitle'
import { BookmarkButton } from 'web_core/assets/bookmark'
import { CollaboratorList } from 'web_core/assets/content/CollaboratorList'
import { CustomAdsCard } from 'web_core/assets/custom_ads/CustomAdsCard'
import { GoodButton } from 'web_core/assets/evaluation'
import { AdsCard } from 'web_core/assets/google_adsense/AdsCard'
import { PageTitleLink, Pagination } from 'web_core/assets/page'
import { LoadingSpinner } from 'web_core/assets/spinners'
import { ActiveContentsText } from 'web_core/assets/table/ActiveContentsText'
import { ContentsTable } from 'web_core/assets/table/ContentsTable'
import { SidebarContentsTable } from 'web_core/assets/table/SidebarContentsTable'
import { UserImageWithName } from 'web_core/assets/user'
import { useAccount } from 'web_core/context/account'
import { EvaluationProvider, useEvaluation } from 'web_core/context/evaluation'
import { PrintSettingProvider } from 'web_core/context/print_setting'
import { Article, Book, Page, Profile } from 'web_core/firebase'
import { useElementSize } from 'web_core/hooks'
import {
  ContentsTableData,
  useContentsTable,
} from 'web_core/hooks/useContentsTable'
import { shouldShowAdsInContent } from 'web_core/models/user'
import { Content } from 'web_core/shared/firebase/firestore/scheme/content'
import { Designed } from 'web_core/types'
import { ADSENSE_SLOTS } from 'web_core/utils'
import { DefaultLayout } from '~/components/Layout/Default'
import { useLayoutProperty } from '~/context/layout_property'

type Props = {
  contentRef: React.RefObject<HTMLDivElement>
  profile: Profile | undefined
  isNotFound: boolean
  content: Content | undefined
  pages?: Page[]
  currentPage?: Page
  children: React.ReactNode
  head: HeadProps
}

export const ContentLayout = ({
  contentRef,
  isNotFound,
  content,
  pages,
  profile,
  currentPage,
  children,
  head,
}: Props) => {
  const { headerHeight } = useLayoutProperty()
  const contentsTable = useContentsTable(
    contentRef,
    headerHeight,
    content?.parent_id === 'books' ? currentPage : content
  )
  const currentPageIndex = pages?.findIndex((p) => p.id === currentPage?.id)

  if (isNotFound) return <DefaultErrorPage statusCode={404} />

  return (
    <DefaultLayout
      head={head}
      maxWidth={1300}
      header={{ pc: { fixed: false }, sp: { fixed: false } }}
      padding={{ sp: false, pc: false }}
    >
      <PrintSettingProvider>
        {content && profile ? (
          <EvaluationProvider content={content}>
            <MobileMenu
              contentsTableData={contentsTable}
              content={content}
              className="sticky-top d-lg-none d-print-none"
            />
            <Stack direction="horizontal" className="align-items-stretch gap-5">
              <LeftSidebar content={content} />
              <div className="flex-grow-1 overflow-hidden pt-2 pt-xl-4 px-2 px-xl-0">
                {children}
              </div>
              <RightSidebar
                content={content}
                profile={profile}
                pages={pages}
                currentPage={currentPage}
                currentPageIndex={currentPageIndex}
                contentsTable={contentsTable}
              />
            </Stack>
            {content.parent_id === 'books' &&
              pages &&
              currentPage &&
              currentPageIndex !== undefined && (
                <MobileBottomMenu
                  book={content}
                  pages={pages}
                  currentPageIndex={currentPageIndex}
                />
              )}
          </EvaluationProvider>
        ) : (
          <LoadingSpinner />
        )}
      </PrintSettingProvider>
    </DefaultLayout>
  )
}

type MobileMenuProps = {
  contentsTableData: ContentsTableData
  content: Content
}

export const MobileMenu = ({
  contentsTableData,
  content,
  ...designProps
}: Designed<MobileMenuProps>) => {
  const { goodCount, handleChangeGoodCount } = useEvaluation()
  const [open, setOpen] = useState(false)
  const { headings, activeIndex } = contentsTableData
  const isLoading = activeIndex === undefined || headings === undefined
  return (
    <div {...designProps}>
      <Navbar className="border-bottom flex-column align-items-start w-100 mw-100 overflow-hidden bg-body-tertiary">
        <Container className="px-2 px-lg-4 gap-2 pe-3" fluid>
          <div className="flex-grow-1 overflow-hidden">
            <Button
              variant="link"
              className="d-flex mw-100 text-muted gap-1"
              onClick={() => {
                if (!isLoading && headings.length > 0) setOpen(!open)
              }}
              aria-expanded={open}
            >
              {!isLoading && headings.length > 0 && (
                <IconWrapper className="flex-shrink-0">
                  <FaCaretDown />
                </IconWrapper>
              )}
              <ActiveContentsText
                data={contentsTableData}
                className="flex-grow-1 text-truncate"
                defaultText={content.title}
              />
            </Button>
          </div>
          <div className="flex-shrink-0">
            <div className="d-flex align-items-center gap-4">
              <CopyLinkButton className="fs-3 p-0" />
              <BookmarkButton className="fs-3 p-0" bookmarkable={content} />
              <GoodButton
                className="fs-3 p-0"
                evaluable={content}
                currentGoodCount={goodCount}
                onChangeGoodCount={handleChangeGoodCount}
              />
            </div>
          </div>
        </Container>
        <Collapse in={open}>
          <div
            className="w-100 overflow-auto mt-2 px-2 px-lg-4"
            style={{ maxHeight: '50vh' }}
          >
            <div className="text-break pb-2">{content.title}</div>
            <ContentsTable
              data={contentsTableData}
              onClick={(heading) => {
                setOpen(false)
                heading.scroll()
              }}
            />
          </div>
        </Collapse>
      </Navbar>
    </div>
  )
}

type LeftSidebarProps = {
  content: Content
}

const LeftSidebar = ({ content }: LeftSidebarProps) => {
  const { goodCount, handleChangeGoodCount } = useEvaluation()

  return (
    <div className="flex-shrink-0 d-print-none d-none d-lg-block">
      <div className="sticky-top pt-7 ps-2 ps-md-4 pe-4">
        <Stack className="align-items-center gap-7">
          <div className="text-center">
            <GoodButton
              evaluable={content}
              currentGoodCount={goodCount}
              onChangeGoodCount={handleChangeGoodCount}
              className="p-0 d-block"
              style={{ fontSize: '2rem' }}
            />
            <span className="text-muted">{goodCount}</span>
          </div>
          <BookmarkButton
            className="p-0"
            style={{ fontSize: '2rem' }}
            bookmarkable={content}
          />
          <CopyLinkButton className="p-0" style={{ fontSize: '2rem' }} />

          <PrintButton
            className="p-0 text-muted"
            style={{ fontSize: '2rem' }}
          />
        </Stack>
      </div>
    </div>
  )
}

type BookPageRightSidebarContentsProps = {
  book: Book
  profile: Profile
  pages: Page[]
  currentPage: Page
  currentPageIndex: number
  contentsTable: ContentsTableData
}

const BookPageRightSidebarContents = ({
  book,
  profile,
  pages,
  currentPage,
  currentPageIndex,
  contentsTable,
}: BookPageRightSidebarContentsProps) => {
  const router = useRouter()

  return (
    <>
      <UserImageWithName profile={profile} size={25} gap={2} className="mb-2" />
      <CollaboratorList
        collaboratorUids={book.collaborator_uids}
        className="mb-2"
      />
      <BookImageWithTitle book={book} width={72} />
      <div className="pt-3 pb-2">
        <p className="text-muted mb-0 fs-7">現在のページ</p>
        <PageTitleLink
          base={`/books/${book.id}`}
          page={currentPage}
          className="text-body text-break"
        />
      </div>
      <SidebarContentsTable
        data={contentsTable}
        className="d-flex flex-column overflow-hidden"
      />
      <Pagination
        pages={pages}
        currentPageIndex={currentPageIndex}
        onClickPrev={async () => {
          await router.push(
            `/books/${book.id}/${pages[currentPageIndex - 1].id}`
          )
        }}
        onClickNext={async () => {
          await router.push(
            `/books/${book.id}/${pages[currentPageIndex + 1].id}`
          )
        }}
        className="d-flex fs-7 text-muted align-items-center justify-content-between mt-4"
      />
    </>
  )
}

type ArticleRightSidebarContentsProps = {
  article: Article
  profile: Profile
  contentsTable: ContentsTableData
}

const ArticleRightSidebarContents = ({
  article,
  profile,
  contentsTable,
}: ArticleRightSidebarContentsProps) => {
  return (
    <>
      <UserImageWithName profile={profile} size={25} gap={2} className="mb-2" />

      <div className="pb-2">
        <ArticleTitleLink article={article} className="text-body text-break" />
      </div>
      <SidebarContentsTable
        data={contentsTable}
        className="d-flex flex-column overflow-hidden"
      />
    </>
  )
}

type RightSidebarProps = {
  content: Content
  profile: Profile
  pages?: Page[]
  currentPage?: Page
  currentPageIndex?: number
  contentsTable: ContentsTableData
}

const RightSidebar = ({
  content,
  profile,
  pages,
  currentPage,
  currentPageIndex,
  contentsTable,
}: RightSidebarProps) => {
  const { account } = useAccount()

  return (
    <div className="flex-shrink-0 d-none d-xl-block d-print-none pt-2 pt-xl-4 pe-2 pe-md-4">
      {/* サイドバー広告１ */}
      {shouldShowAdsInContent(profile.plan, account?.plan) && (
        // プレミアムプランバナーは OptHub採用エントリー広告出稿のため一時的に非表示
        // <div
        //   className="flex-shrink-0 d-none d-print-none py-3 d-xl-flex flex-column"
        //   style={{ width: 320 }}
        // >
        //   <Link href="/pricing" passHref>
        //     <a
        //       onClick={() => {
        //         // バナーがクリックされた際のイベント収集
        //         logEvent('tap_content_sidebar_banner')
        //       }}
        //     >
        //       <Image
        //         src="/images/premium_banner_640x440.png"
        //         alt="もっと数学に没頭できる! Mathlog Premiumプラン! 1週間無料体験"
        //         width={640}
        //         height={440}
        //       />
        //     </a>
        //   </Link>
        // </div>
        <div style={{ maxWidth: 320 }}>
          {/* サイドバー広告１ */}
          <CustomAdsCard isLandscape={false} />
          {/* サイドバー広告２ */}
          <AdsCard
            height="250px"
            className="mb-3 flex-shrink-0"
            slot={ADSENSE_SLOTS.CONTENT_SIDEBAR_2_SLOT}
          />
        </div>
      )}
      <div
        className="sticky-top overflow-hidden d-flex flex-column py-3 dvh-100"
        style={{ width: 320 }}
      >
        {content.parent_id === 'articles' ? (
          <ArticleRightSidebarContents
            article={content}
            profile={profile}
            contentsTable={contentsTable}
          />
        ) : pages && currentPage && currentPageIndex !== undefined ? (
          <BookPageRightSidebarContents
            book={content}
            profile={profile}
            pages={pages}
            currentPage={currentPage}
            currentPageIndex={currentPageIndex}
            contentsTable={contentsTable}
          />
        ) : (
          <></>
        )}
      </div>
    </div>
  )
}

type MobileBottomMenuProps = {
  book: Book
  pages: Page[]
  currentPageIndex: number
}
const MobileBottomMenu = ({
  book,
  pages,
  currentPageIndex,
}: MobileBottomMenuProps) => {
  const router = useRouter()
  const [show, setShow] = useState(false)
  const handleClose = () => setShow(false)
  const handleShow = () => setShow(true)
  const [isLoading, setIsLoading] = useState(false)

  const { elementRef: mobileBottomMenuRef, height: mobileBottomMenuHeight } =
    useElementSize()

  type MovePageButtonProps = {
    book: Book
    pages: Page[]
    currentPageIndex: number
  }

  const PrevButton = ({
    book,
    pages,
    currentPageIndex,
    ...wrapperProps
  }: Designed<MovePageButtonProps>) => {
    return currentPageIndex > 0 ? (
      <div
        {...wrapperProps}
        onClick={async () => {
          await router.push(
            `/books/${book.id}/${pages[currentPageIndex - 1].id}`
          )
        }}
      >
        <div className="d-flex fs-3 text-muted">
          <FaCaretLeft />
        </div>
        <div className="d-flex fs-7 text-muted">前ページへ</div>
      </div>
    ) : (
      <div {...wrapperProps} />
    )
  }

  const NextButton = ({
    book,
    pages,
    currentPageIndex,
    ...wrapperProps
  }: Designed<MovePageButtonProps>) => {
    return currentPageIndex < pages.length - 1 ? (
      <div
        {...wrapperProps}
        onClick={async () => {
          await router.push(
            `/books/${book.id}/${pages[currentPageIndex + 1].id}`
          )
        }}
      >
        <div className="d-flex fs-7 text-muted">次ページへ</div>
        <div className="d-flex fs-3 text-muted">
          <FaCaretRight />
        </div>
      </div>
    ) : (
      <div {...wrapperProps} />
    )
  }

  return (
    <>
      <div
        className="d-xl-none bg-light fixed-bottom w-100 border-top shadow-lg d-print-none"
        ref={mobileBottomMenuRef}
      >
        <div className="container">
          <div className="row" style={{ height: 40 }}>
            <PrevButton
              book={book}
              pages={pages}
              currentPageIndex={currentPageIndex}
              className="col d-flex align-items-center justify-content-center cursor-pointer"
            />
            <div
              className="col d-flex align-items-center justify-content-center cursor-pointer border-start border-end"
              onClick={handleShow}
            >
              <span className="mb-0 fs-7">
                <BookImage book={book} width={25} />
              </span>
            </div>
            <NextButton
              book={book}
              pages={pages}
              currentPageIndex={currentPageIndex}
              className="col d-flex align-items-center justify-content-center cursor-pointer"
            />
          </div>
        </div>
        <Offcanvas
          show={show}
          onHide={handleClose}
          placement={'bottom'}
          style={{ minHeight: '60svh' }}
          scroll
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title className="text-break text-truncate-3 pe-2">
              {book.title}
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <ol>
                {pages.map((page) => (
                  <li
                    className="mb-2"
                    key={page.id}
                    onClick={async () => {
                      setIsLoading(true)
                      await router.push(`/books/${book.id}/${page.id}`)
                      handleClose()
                      setIsLoading(false)
                    }}
                  >
                    <span className="cursor-pointer text-primary">
                      {page.title}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </Offcanvas.Body>
        </Offcanvas>
      </div>
      <Container
        style={{ height: mobileBottomMenuHeight }} // MobileBottomMenu の高さ分
        className="d-xl-none d-print-none"
        fluid
      />
    </>
  )
}
